import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import {
  FileText,
  Calendar,
  User,
  BarChart3,
  Link as LinkIcon,
  Play,
  Trash2,
  ArrowLeft,
  AlertCircle,
  Loader2,
} from "lucide-react";
import { useTemplateDetail } from "../../../hooks";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { useDeleteMigrationTemplate } from "../../../hooks";
import { PageHeader } from "@shared/components/PageHeader";

export const TemplateDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Fetch template details from API
  const { data, isLoading, error, refetch } = useTemplateDetail(id || "");
  const { mutate: deleteTemplate, isPending: isDeleting } =
    useDeleteMigrationTemplate();

  const handleUseTemplate = () => {
    navigate("/onboarding/data-migration/file-upload", {
      state: {
        templateId: data.id,
        dataType: data.dataType,
        operation_type: 2, // two is for new upload
        categoryKey: data.dataType,
        uploadMethod: "template",
      },
    });
  };

  const handleDeleteTemplate = () => {
    deleteTemplate(id || "", {
      onSuccess: () => {
        navigate(-1);
      },
    });
  };

  const getConfidenceBadgeColor = (confidence: number) => {
    if (confidence >= 85) return "bg-green-100 text-green-700 border-green-200";
    if (confidence >= 75)
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    return "bg-orange-100 text-orange-700 border-orange-200";
  };

  // Transform API data to match UI structure
  const mappings = React.useMemo(() => {
    if (!data?.columnTransformationMapping) return [];

    return Object.entries(data.columnTransformationMapping).map(
      ([targetField, mapping]) => {
        return {
          sourceField: mapping?.sourceColumn || mapping?.source_column,
          targetField: toSnakeCase(targetField),
          confidence: mapping?.confidenceScore,
          transformations:
            mapping?.transformationType?.length > 0
              ? mapping?.transformationType?.join(", ")
              : "—",
        };
      }
    );
  }, [data]);

  const columnsMapped = mappings.length;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
          <p className="text-gray-600">Loading template details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="bg-red-100 text-red-700 px-4 py-3 rounded-lg mb-4">
            Failed to load template details. Please try again.
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-4 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="h-8 w-8"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <PageHeader
                title="Template Details"
                subtitle="View and manage template configuration"
              />
            </div>
          </div>
        </div>

        {/* Quick Actions - Top Right */}
        <div className="flex justify-end gap-3">
          <Button
            variant="default"
            onClick={handleUseTemplate}
            className="gap-2"
          >
            <Play className="h-4 w-4" />
            Use Template
          </Button>
          <Button
            variant="destructive"
            onClick={handleDeleteTemplate}
            className="gap-2"
          >
            <Trash2 className="h-4 w-4" />
            Delete Template
          </Button>
        </div>

        {/* Template Details Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-6">
              <FileText className="h-5 w-5 text-gray-500" />
              <h3 className="text-lg font-semibold text-gray-900">
                Template Details
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Template ID */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span>#</span>
                  <span>Template ID</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {data.templateNo}
                </p>
              </div>

              {/* Template Name */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FileText className="h-4 w-4" />
                  <span>Template Name</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {data.name}
                </p>
              </div>

              {/* Data Type */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <FileText className="h-4 w-4" />
                  <span>Data Type</span>
                </div>
                <Badge variant="default" className="font-medium">
                  {data.dataTypeDisplay}
                </Badge>
              </div>

              {/* Created By */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <User className="h-4 w-4" />
                  <span>Created By</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {data.createdUserRemoteName}
                </p>
              </div>

              {/* Created On */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Calendar className="h-4 w-4" />
                  <span>Created On</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {new Date(data.createdDate).toLocaleDateString()}
                </p>
              </div>

              {/* Usage Count */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <BarChart3 className="h-4 w-4" />
                  <span>Usage Count</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {data.usageCount} times
                </p>
              </div>

              {/* Columns Mapped */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <LinkIcon className="h-4 w-4" />
                  <span>Columns Mapped</span>
                </div>
                <p className="text-base font-medium text-gray-900">
                  {columnsMapped} fields mapped
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mapping Configuration Summary */}
        <Card>
          <CardContent className="pt-6">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                Mapping Configuration Summary
              </h3>
              <p className="text-sm text-gray-500">
                Review field mappings, confidence and transformations
              </p>
            </div>

            {/* Table */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Source Field
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Target Field
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Confidence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transformations
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {mappings.map((mapping, index) => {
                    return (
                      <tr
                        key={index}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">
                            {mapping.sourceField}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <code className="text-sm font-mono text-green-700 bg-green-50 px-2 py-1 rounded">
                            {mapping.targetField}
                          </code>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Badge
                            variant="outline"
                            className={`${getConfidenceBadgeColor(
                              mapping.confidence
                            )} font-medium`}
                          >
                            {mapping.confidence}%
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            {mapping.transformations === "—" ? (
                              <span className="text-sm text-gray-400">
                                {mapping.transformations}
                              </span>
                            ) : (
                              <Badge
                                variant="secondary"
                                className="font-normal text-xs"
                              >
                                {mapping.transformations}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Info Note */}
            <div className="mt-4 flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Template Usage</p>
                <p>
                  When you use this template, all column mappings and
                  transformation rules will be pre-applied. You can still modify
                  them before finalizing the data migration.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
